import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { fromJS, Map } from 'immutable';
import { Subject, Subscription } from 'rxjs';
import { LoggerService } from '../shared/services/logger/logger.service';
import { debounceTime, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-form-master',
  templateUrl: 'form-master.component.html',
  styleUrls: ['form-master.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormMasterComponent implements OnChanges, OnDestroy {
  @Input() form: FormGroup;
  @Input() preventSubmitOnEnter: boolean;
  @Input() debounceTime = 0;

  @Output() formValidity: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() rawFormChanges: EventEmitter<Map<string, any>> = new EventEmitter<
    Map<string, any>
  >();
  @Output() formChanges: EventEmitter<Map<string, any>> = new EventEmitter<
    Map<string, any>
  >();
  @Output() validFormChanges: EventEmitter<Map<string, any>> = new EventEmitter<
    Map<string, any>
  >();
  @Output() submitForm: EventEmitter<Map<string, any>> = new EventEmitter<
    Map<string, any>
  >();

  private _destroyed: Subject<boolean> = new Subject<boolean>();
  private _submitting = false;
  private _formSub: Subscription;

  constructor(private _loggerService: LoggerService) {}

  ngOnChanges(changes) {
    if (changes.form && this.form) {
      this.setUpForm(this.form);
    }
  }

  setUpForm(form: FormGroup) {
    this.subscribeToFormChanges(form);

    this.checkFormValidity(form);
  }

  subscribeToFormChanges(form = this.form) {
    this._formSub && this._formSub.unsubscribe();

    this._formSub = form.valueChanges
      .pipe(takeUntil(this._destroyed), debounceTime(this.debounceTime))
      .subscribe((changes) => {
        if (form.valid) {
          this.validFormChanges.emit(fromJS(changes));
        }

        this.formChanges.emit(fromJS(changes));

        // getRawValue() includes all field values (including disabled ones)
        this.rawFormChanges.emit(fromJS(form.getRawValue()));

        this.checkFormValidity();
      });
  }

  checkFormValidity(form = this.form) {
    this.formValidity.emit(form.valid);
  }

  onSubmit(evt: Event) {
    if (this.form.valid && this.form.enabled) {
      this._loggerService.log('submitting form', this.form.value);

      this._submitting = true;

      evt.preventDefault();

      this.submitForm.emit(fromJS(this.form.value));
    } else {
      this._loggerService.log('tried to submit form, but form is not valid');
    }
  }

  ngOnDestroy() {
    this._formSub && this._formSub.unsubscribe();

    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
